import {
  makeStyles,
  RACButton,
  RACCard,
  RACCOLOR,
} from '@rentacenter/racstrap';
import { get } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  PoFormField,
  PoFormLabel,
  VALIDATIONS,
} from '../../constants/constants';
import { PurchaseOrderFormValues, QrScannerOutput } from '../../types/types';
import { Bracket } from '../InventoryDetailsItems/Bracket';
import { ClassificationsType } from '../InventoryDetailsItems/ClassificationsType';
import { Provider } from '../InventoryDetailsItems/context/Provider';
import { Department } from '../InventoryDetailsItems/Department';
import { InitialClassificationsSetter } from '../InventoryDetailsItems/InitialClassificationsSetter';
import { SubDepartment } from '../InventoryDetailsItems/SubDepartment';
import { SubType } from '../InventoryDetailsItems/SubType';
import { FieldWithTooltip } from '../../common/FieldWithTooltip/FieldWithTooltip';
import { AutoNumeric } from '../../common/AutoNumeric/AutoNumeric';

export interface LocalSourceItemDetailsProps {
  purchaseOrder?: PurchaseOrderFormValues | null;
  disabled: boolean;
  qrScannerOutput: QrScannerOutput | undefined;
  setIsCoupaOrPcrdSelected?: boolean | undefined;
  index?: number;
  canRemove?: boolean;
  onRemove?: () => void;
}

const testId = 'inventoryTestId';
const useStyles = makeStyles((theme: any) => ({
  root: {
    marginBottom: '1.5rem',
  },
  row: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    marginTop: theme.typography.pxToRem(8),
  },
  cardContentContainer: {
    padding: '1.25rem',
    display: 'flex',
    gap: theme.typography.pxToRem(16),
    justifyContent: 'flex-start',
    flexWrap: 'wrap',
  },
  field: {
    width: theme.typography.pxToRem(200),
  },
  smallField: {
    width: theme.typography.pxToRem(80),
  },
  errorMessage: {
    position: 'absolute',
  },
  removeButton: {
    backgroundColor: RACCOLOR.LINEN,
    color: RACCOLOR.PUNCH,
    alignSelf: 'flex-start',
    marginTop: theme.typography.pxToRem(22),
    width: theme.typography.pxToRem(97),
    height: theme.typography.pxToRem(35),
    '&:hover': {
      backgroundColor: RACCOLOR.LINEN,
    },
    '&:disabled': {
      background: 'rgba(233,92,92,255)',
      opacity: 0.5,
    },
  },
}));

export const LocalSourceInventoryDetails = ({
  purchaseOrder,
  disabled,
  qrScannerOutput,
  index = 0,
  canRemove = false,
  onRemove,
  setIsCoupaOrPcrdSelected,
}: LocalSourceItemDetailsProps) => {
  const classes = useStyles();
  const { register, getValues, errors, setValue } = useFormContext();
  const allocations = useWatch({ name: 'allocations' }) as any[];
  const primaryStoreNum = allocations?.[0]?.storeNum;
  const primaryState = allocations?.[0]?.state;
  const getFieldName = (fieldName: PoFormField) =>
    index === 0 ? fieldName : `products[${index}].${fieldName}`;
  const purchaseOrderForDefaultValues = purchaseOrder || null;

  const errorMessage = get(errors, `allocations[${index}].quantity`)
    ? VALIDATIONS[PoFormField.Quantity]
    : '';
  const costPerItemName = getFieldName(PoFormField.CostPerItem);
  const costPerItemErrorType = get(errors, `${costPerItemName}.type`);
  const startClassificationsDefaultValue =
    purchaseOrderForDefaultValues?.startClassificationsSelect ||
    (index === 0 ? (getValues().startClassificationsSelect as string) : '');
  const storeNumFieldName = `allocations[${index}].storeNum`;
  const stateFieldName = `allocations[${index}].state`;

  useEffect(() => {
    if (index === 0 || !primaryStoreNum || !primaryState) {
      return;
    }

    setValue(storeNumFieldName, primaryStoreNum, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue(stateFieldName, primaryState, {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [
    index,
    primaryState,
    primaryStoreNum,
    setValue,
    stateFieldName,
    storeNumFieldName,
  ]);

  // Set classification form values from purchaseOrder prop (only on first load)
  const classificationFieldsSeeded = useRef(false);
  useEffect(() => {
    if (!purchaseOrderForDefaultValues || classificationFieldsSeeded.current)
      return;
    classificationFieldsSeeded.current = true;
    const fields = [
      {
        field: PoFormField.Department,
        value: purchaseOrderForDefaultValues.department,
      },
      {
        field: PoFormField.SubDepartment,
        value: purchaseOrderForDefaultValues.subDepartment,
      },
      {
        field: PoFormField.Bracket,
        value: purchaseOrderForDefaultValues.bracket,
      },
      {
        field: PoFormField.ItemType,
        value: purchaseOrderForDefaultValues.itemType,
      },
      {
        field: PoFormField.ItemSubType,
        value: purchaseOrderForDefaultValues.itemSubType,
      },
      {
        field: PoFormField.StartClassificationsSelect,
        value: purchaseOrderForDefaultValues.startClassificationsSelect,
      },
    ];
    fields.forEach(({ field, value }) => {
      if (value) {
        setValue(getFieldName(field), value);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseOrderForDefaultValues]);

  return (
    <Provider>
      <InitialClassificationsSetter />
      <div className={classes.root} data-testid={testId}>
        <RACCard>
          <div className={classes.cardContentContainer}>
            <span className={classes.field}>
              <Department
                isCoupaOrPcrd={setIsCoupaOrPcrdSelected}
                qrScannerOutput={qrScannerOutput}
                name={getFieldName(PoFormField.Department)}
                label={PoFormLabel.Department}
                purchaseOrder={purchaseOrderForDefaultValues}
                showErrorMessage
              />
            </span>
            <span className={classes.field}>
              <SubDepartment
                qrScannerOutput={qrScannerOutput}
                name={getFieldName(PoFormField.SubDepartment)}
                label={PoFormLabel.SubDepartment}
                purchaseOrder={purchaseOrderForDefaultValues}
                showErrorMessage
              />
            </span>
            <span className={classes.field}>
              <Bracket
                qrScannerOutput={qrScannerOutput}
                name={getFieldName(PoFormField.Bracket)}
                label={PoFormLabel.Bracket}
                purchaseOrder={purchaseOrderForDefaultValues}
                showErrorMessage
              />
            </span>
            <span className={classes.field}>
              <ClassificationsType
                qrScannerOutput={qrScannerOutput}
                name={getFieldName(PoFormField.ItemType)}
                label={PoFormLabel.Type}
                purchaseOrder={purchaseOrderForDefaultValues}
                showErrorMessage
              />
            </span>
            <span className={classes.field}>
              <SubType
                qrScannerOutput={qrScannerOutput}
                name={getFieldName(PoFormField.ItemSubType)}
                label={PoFormLabel.SubType}
                purchaseOrder={purchaseOrderForDefaultValues}
                showErrorMessage
              />
            </span>
            <span className={classes.smallField}>
              <FieldWithTooltip
                id={PoFormLabel.Quantity}
                title={PoFormLabel.Quantity}
                required
                errorMessage={errorMessage}
              >
                <Controller
                  as={AutoNumeric}
                  name={`allocations[${index}].quantity`}
                  options={{
                    minimumValue: 0,
                    maximumValue: 9999,
                    decimalPlaces: 0,
                    restrictZeroFirstChar: true,
                  }}
                  disabled={disabled}
                  rules={{ required: true, min: 1 }}
                />
              </FieldWithTooltip>
            </span>
            <span className={classes.field}>
              <FieldWithTooltip
                id={costPerItemName}
                currency
                title={PoFormLabel.CostPerItem}
                required
                errorMessage={
                  costPerItemErrorType
                    ? VALIDATIONS[PoFormField.CostPerItem]
                    : ''
                }
              >
                <Controller
                  as={AutoNumeric}
                  name={costPerItemName}
                  defaultValue={
                    purchaseOrderForDefaultValues?.costPerItem || ''
                  }
                  options={{
                    minimumValue: 0,
                    maximumValue: 9999.0,
                    decimalPlaces: 2,
                  }}
                  disabled={disabled}
                  rules={{ required: true, min: 1 }}
                  hasError={!!costPerItemErrorType}
                  changeOnBlureOnly
                />
              </FieldWithTooltip>
            </span>
            <RACButton
              className={classes.removeButton}
              disabled={disabled || !canRemove}
              size="small"
              variant="contained"
              color="primary"
              onClick={onRemove}
            >
              Remove
            </RACButton>
            <input
              type="hidden"
              name={getFieldName(PoFormField.StartClassificationsSelect)}
              ref={register}
              defaultValue={startClassificationsDefaultValue}
            />
            <input
              type="hidden"
              name={getFieldName(PoFormField.SubDepartment) + '__unresolved'}
              ref={register}
            />
            <input
              type="hidden"
              name={getFieldName(PoFormField.Bracket) + '__unresolved'}
              ref={register}
            />
            <input
              type="hidden"
              name={getFieldName(PoFormField.ItemType) + '__unresolved'}
              ref={register}
            />
            <input
              type="hidden"
              name={getFieldName(PoFormField.ItemSubType) + '__unresolved'}
              ref={register}
            />
            {index > 0 && (
              <>
                <input
                  type="hidden"
                  name={storeNumFieldName}
                  ref={register}
                  defaultValue={primaryStoreNum || ''}
                />
                <input
                  type="hidden"
                  name={stateFieldName}
                  ref={register}
                  defaultValue={primaryState || ''}
                />
              </>
            )}
          </div>
        </RACCard>
      </div>
    </Provider>
  );
};
